import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { ScrollArea, ScrollBar } from '../components/ui/scroll-area';
import { LessonModeBadge } from '../components/LessonModeBadge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  ArrowLeft,
  Download,
  CheckCircle2,
  XCircle,
  Minus,
  Clock,
  Edit3,
  FileText,
  Image as ImageIcon,
  Video,
  CheckSquare,
  Target,
  PenTool,
  Cloud,
  Gamepad2,
  MessageSquare,
} from 'lucide-react';
import { toast } from 'sonner';

interface ActivityScore {
  activityId: string;
  score: number | null;
  completed: boolean;
  timeSpent?: number;
  autoGraded: boolean;
}

interface StudentActivityData {
  studentId: string;
  studentName: string;
  avatar: string;
  activities: ActivityScore[];
  totalScore: number;
  manualOverride?: number;
}

const activityTypes = [
  { id: 'quiz-1', name: 'Quiz 1', icon: CheckSquare, type: 'quiz', autoGrade: true },
  { id: 'poll-1', name: 'Poll', icon: MessageSquare, type: 'poll', autoGrade: false },
  { id: 'matching-1', name: 'Matching Pairs', icon: Target, type: 'matching', autoGrade: true },
  { id: 'climb-1', name: 'Time to Climb', icon: Gamepad2, type: 'game', autoGrade: true },
  { id: 'draw-1', name: 'Draw-It', icon: PenTool, type: 'draw', autoGrade: false },
  { id: 'open-1', name: 'Open Response', icon: FileText, type: 'open', autoGrade: false },
  { id: 'word-cloud', name: 'Word Cloud', icon: Cloud, type: 'wordcloud', autoGrade: false },
];

export default function GradebookLessonView() {
  const navigate = useNavigate();
  const [selectedLesson, setSelectedLesson] = useState('lesson-1');
  const [showManualOverride, setShowManualOverride] = useState<string | null>(null);

  const [studentsData] = useState<StudentActivityData[]>([
    {
      studentId: '1',
      studentName: 'Sarah Johnson',
      avatar: 'bg-blue-500',
      activities: [
        { activityId: 'quiz-1', score: 95, completed: true, timeSpent: 180, autoGraded: true },
        { activityId: 'poll-1', score: null, completed: true, timeSpent: 45, autoGraded: false },
        { activityId: 'matching-1', score: 100, completed: true, timeSpent: 120, autoGraded: true },
        { activityId: 'climb-1', score: 90, completed: true, timeSpent: 240, autoGraded: true },
        { activityId: 'draw-1', score: 85, completed: true, timeSpent: 300, autoGraded: false },
        { activityId: 'open-1', score: 92, completed: true, timeSpent: 360, autoGraded: false },
        { activityId: 'word-cloud', score: null, completed: true, timeSpent: 90, autoGraded: false },
      ],
      totalScore: 92,
    },
    {
      studentId: '2',
      studentName: 'Michael Chen',
      avatar: 'bg-green-500',
      activities: [
        { activityId: 'quiz-1', score: 85, completed: true, timeSpent: 200, autoGraded: true },
        { activityId: 'poll-1', score: null, completed: true, timeSpent: 30, autoGraded: false },
        { activityId: 'matching-1', score: 90, completed: true, timeSpent: 150, autoGraded: true },
        { activityId: 'climb-1', score: 75, completed: true, timeSpent: 280, autoGraded: true },
        { activityId: 'draw-1', score: null, completed: true, timeSpent: 250, autoGraded: false },
        { activityId: 'open-1', score: 88, completed: true, timeSpent: 320, autoGraded: false },
        { activityId: 'word-cloud', score: null, completed: true, timeSpent: 60, autoGraded: false },
      ],
      totalScore: 85,
    },
    {
      studentId: '3',
      studentName: 'Emily Rodriguez',
      avatar: 'bg-purple-500',
      activities: [
        { activityId: 'quiz-1', score: 90, completed: true, timeSpent: 160, autoGraded: true },
        { activityId: 'poll-1', score: null, completed: true, timeSpent: 40, autoGraded: false },
        { activityId: 'matching-1', score: 95, completed: true, timeSpent: 110, autoGraded: true },
        { activityId: 'climb-1', score: 88, completed: true, timeSpent: 250, autoGraded: true },
        { activityId: 'draw-1', score: 90, completed: true, timeSpent: 280, autoGraded: false },
        { activityId: 'open-1', score: null, completed: false, autoGraded: false },
        { activityId: 'word-cloud', score: null, completed: true, timeSpent: 75, autoGraded: false },
      ],
      totalScore: 89,
    },
    {
      studentId: '4',
      studentName: 'James Wilson',
      avatar: 'bg-orange-500',
      activities: [
        { activityId: 'quiz-1', score: 78, completed: true, timeSpent: 220, autoGraded: true },
        { activityId: 'poll-1', score: null, completed: true, timeSpent: 35, autoGraded: false },
        { activityId: 'matching-1', score: 80, completed: true, timeSpent: 180, autoGraded: true },
        { activityId: 'climb-1', score: 70, completed: true, timeSpent: 300, autoGraded: true },
        { activityId: 'draw-1', score: 75, completed: true, timeSpent: 290, autoGraded: false },
        { activityId: 'open-1', score: 80, completed: true, timeSpent: 340, autoGraded: false },
        { activityId: 'word-cloud', score: null, completed: true, timeSpent: 55, autoGraded: false },
      ],
      totalScore: 78,
    },
  ]);

  const getScoreColor = (score: number) => {
    if (score >= 90) return 'text-green-600';
    if (score >= 80) return 'text-emerald-600';
    if (score >= 70) return 'text-orange-600';
    if (score >= 60) return 'text-amber-600';
    return 'text-red-600';
  };

  const getActivityIcon = (activityId: string) => {
    const activity = activityTypes.find(a => a.id === activityId);
    return activity?.icon || FileText;
  };

  const handleExport = () => {
    toast.success('Exporting lesson gradebook...');
  };

  const handleGradeActivity = (studentId: string, activityId: string) => {
    navigate(`/gradebook/grade/${studentId}/${activityId}`);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-card border-b border-border sticky top-0 z-10">
        <div className="max-w-[1600px] mx-auto px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                onClick={() => navigate('/gradebook')}
                className="rounded-xl"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div>
                <h1 className="text-2xl font-semibold text-foreground">Lesson Gradebook</h1>
                <p className="text-sm text-muted-foreground">Activity-by-activity breakdown</p>
              </div>
            </div>
            <Button onClick={handleExport} className="rounded-xl">
              <Download className="w-4 h-4 mr-2" />
              Export
            </Button>
          </div>

          <div className="flex items-center gap-3">
            <Select value={selectedLesson} onValueChange={setSelectedLesson}>
              <SelectTrigger className="w-[300px] rounded-xl">
                <SelectValue placeholder="Select Lesson" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="lesson-1">Introduction to Photosynthesis</SelectItem>
                <SelectItem value="lesson-2">World War II History</SelectItem>
                <SelectItem value="lesson-3">Basic Algebra Concepts</SelectItem>
              </SelectContent>
            </Select>
            <Badge className="rounded-lg bg-primary/10 text-primary border-primary/20">
              7 Activities • {studentsData.length} Students
            </Badge>
          </div>
        </div>
      </header>

      <div className="max-w-[1600px] mx-auto px-6 py-8">
        {/* Legend */}
        <div className="bg-card border border-border rounded-xl p-4 mb-6">
          <div className="flex items-center gap-6 flex-wrap text-sm">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded bg-green-100 flex items-center justify-center">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
              </div>
              <span className="text-muted-foreground">Completed</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded bg-gray-100 flex items-center justify-center">
                <Minus className="w-4 h-4 text-gray-600" />
              </div>
              <span className="text-muted-foreground">Not Submitted</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded bg-blue-100 flex items-center justify-center">
                <span className="text-xs font-semibold text-blue-600">95</span>
              </div>
              <span className="text-muted-foreground">Auto-graded</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded bg-orange-100 flex items-center justify-center">
                <Edit3 className="w-3 h-3 text-orange-600" />
              </div>
              <span className="text-muted-foreground">Manual grading</span>
            </div>
          </div>
        </div>

        {/* Lesson Gradebook Table */}
        <div className="bg-card border border-border rounded-2xl overflow-hidden">
          <ScrollArea className="w-full">
            <div className="min-w-[1200px]">
              {/* Table Header */}
              <div className="grid grid-cols-[200px_repeat(7,120px)_100px] gap-2 px-4 py-4 bg-muted/50 border-b border-border sticky top-0">
                <div className="text-sm font-semibold text-muted-foreground">Student</div>
                {activityTypes.map((activity) => (
                  <div key={activity.id} className="text-center">
                    <div className="flex flex-col items-center gap-1">
                      <activity.icon className="w-5 h-5 text-primary" />
                      <span className="text-xs font-semibold text-muted-foreground truncate w-full">
                        {activity.name}
                      </span>
                      {activity.autoGrade && (
                        <Badge className="text-[10px] px-1 py-0 h-4 bg-blue-100 text-blue-700 border-blue-200">
                          AUTO
                        </Badge>
                      )}
                    </div>
                  </div>
                ))}
                <div className="text-sm font-semibold text-muted-foreground text-center">Total</div>
              </div>

              {/* Table Body */}
              <div className="divide-y divide-border">
                {studentsData.map((student) => (
                  <div
                    key={student.studentId}
                    className="grid grid-cols-[200px_repeat(7,120px)_100px] gap-2 px-4 py-4 hover:bg-accent/5 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full ${student.avatar} flex items-center justify-center text-white text-sm font-semibold`}>
                        {student.studentName.charAt(0)}
                      </div>
                      <span className="font-medium text-foreground text-sm truncate">
                        {student.studentName}
                      </span>
                    </div>

                    {activityTypes.map((activity) => {
                      const activityData = student.activities.find(a => a.activityId === activity.id);
                      
                      if (!activityData?.completed) {
                        return (
                          <div key={activity.id} className="flex items-center justify-center">
                            <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
                              <Minus className="w-4 h-4 text-gray-400" />
                            </div>
                          </div>
                        );
                      }

                      if (activityData.score === null) {
                        return (
                          <div key={activity.id} className="flex items-center justify-center">
                            <button
                              onClick={() => handleGradeActivity(student.studentId, activity.id)}
                              className="w-10 h-10 rounded-lg bg-orange-100 hover:bg-orange-200 flex items-center justify-center transition-colors group relative"
                            >
                              <Edit3 className="w-4 h-4 text-orange-600" />
                              {activityData.timeSpent && (
                                <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-popover border border-border rounded-lg px-2 py-1 text-xs whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none shadow-lg">
                                  <Clock className="w-3 h-3 inline mr-1" />
                                  {Math.floor(activityData.timeSpent / 60)}m {activityData.timeSpent % 60}s
                                </div>
                              )}
                            </button>
                          </div>
                        );
                      }

                      return (
                        <div key={activity.id} className="flex items-center justify-center">
                          <button
                            onClick={() => handleGradeActivity(student.studentId, activity.id)}
                            className={`w-10 h-10 rounded-lg ${
                              activityData.autoGraded ? 'bg-blue-100' : 'bg-green-100'
                            } hover:ring-2 hover:ring-primary/50 flex items-center justify-center transition-all group relative`}
                          >
                            <span className={`text-sm font-semibold ${getScoreColor(activityData.score)}`}>
                              {activityData.score}
                            </span>
                            {activityData.timeSpent && (
                              <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-popover border border-border rounded-lg px-2 py-1 text-xs whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none shadow-lg">
                                <Clock className="w-3 h-3 inline mr-1" />
                                {Math.floor(activityData.timeSpent / 60)}m {activityData.timeSpent % 60}s
                              </div>
                            )}
                          </button>
                        </div>
                      );
                    })}

                    <div className="flex items-center justify-center gap-2">
                      <span className={`text-lg font-bold ${getScoreColor(student.totalScore)}`}>
                        {student.totalScore}%
                      </span>
                      {student.manualOverride && (
                        <Badge className="text-[10px] px-1 py-0 h-4 bg-orange-100 text-orange-700 border-orange-200">
                          M
                        </Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <ScrollBar orientation="horizontal" />
          </ScrollArea>
        </div>

        {/* Quick Stats */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-card border border-border rounded-xl p-4">
            <p className="text-sm text-muted-foreground mb-1">Average Class Score</p>
            <p className="text-2xl font-bold text-foreground">
              {Math.round(studentsData.reduce((sum, s) => sum + s.totalScore, 0) / studentsData.length)}%
            </p>
          </div>
          <div className="bg-card border border-border rounded-xl p-4">
            <p className="text-sm text-muted-foreground mb-1">Completion Rate</p>
            <p className="text-2xl font-bold text-foreground">92%</p>
          </div>
          <div className="bg-card border border-border rounded-xl p-4">
            <p className="text-sm text-muted-foreground mb-1">Needs Manual Grading</p>
            <p className="text-2xl font-bold text-orange-600">8 submissions</p>
          </div>
        </div>
      </div>
    </div>
  );
}